import React, { useCallback, useRef, useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import ImageResize from 'tiptap-extension-resize-image';
import Link from "@tiptap/extension-link";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import { Table } from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import FontFamily from "@tiptap/extension-font-family";
import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";
import { Attachment, DeleteDustbin, ImageIcon } from "../../AllSvg";

// ─── Custom FontSize extension ──────────────────────────────────────────────
const FontSize = Extension.create({
    name: "fontSize",
    addOptions() {
        return { types: ["textStyle"] };
    },
    addGlobalAttributes() {
        return [
            {
                types: this.options.types,
                attributes: {
                    fontSize: {
                        default: null,
                        parseHTML: (element) => element.style.fontSize?.replace(/['"]+/g, "") || null,
                        renderHTML: (attributes) => {
                            if (!attributes.fontSize) return {};
                            return { style: `font-size: ${attributes.fontSize}` };
                        },
                    },
                },
            },
        ];
    },
    addCommands() {
        return {
            setFontSize:
                (fontSize) =>
                    ({ chain }) => {
                        return chain().setMark("textStyle", { fontSize }).run();
                    },
            unsetFontSize:
                () =>
                    ({ chain }) => {
                        return chain().setMark("textStyle", { fontSize: null }).removeEmptyTextStyle().run();
                    },
        };
    },
});


const tableBorderPluginKey = new PluginKey('tableBorderPlugin');
const TableBorderExtension = Extension.create({
    name: 'tableBorderExtension',
    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: tableBorderPluginKey,
                state: {
                    init() { return DecorationSet.empty; },
                    apply(tr, old, oldState, newState) {
                        const decorations = [];
                        newState.doc.descendants((node, pos) => {
                            if (node.type.name === 'table' && node.attrs.borderless) {
                                decorations.push(Decoration.node(pos, pos + node.nodeSize, { class: 'borderless-table-wrapper' }));
                            }
                        });
                        return DecorationSet.create(newState.doc, decorations);
                    }
                },
                props: {
                    decorations(state) {
                        return this.getState(state);
                    }
                }
            })
        ];
    }
});

const CustomTable = Table.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            borderless: {
                default: false,
                parseHTML: element => element.getAttribute('data-borderless') === 'true',
                renderHTML: attributes => {
                    if (attributes.borderless) {
                        return {
                            'data-borderless': 'true',
                            class: 'borderless-table',
                        };
                    }
                    return {};
                },
            },
        };
    },
});

// ─── Font Size Options (matching Quill) ──────────────────────────────────────
const FONT_SIZE_OPTIONS = [
    { label: "10px", value: "10px" },
    { label: "12px", value: "12px" },
    { label: "14px", value: "14px" },
    { label: "16px", value: "16px" },
    { label: "18px", value: "18px" },
    { label: "24px", value: "24px" },
    { label: "32px", value: "32px" },
    { label: "42px", value: "42px" },
];

const FONT_FAMILY_OPTIONS = [
    { label: "Default", value: "" },
    { label: "Sans Serif", value: "Arial, Helvetica, sans-serif" },
    { label: "Serif", value: "Georgia, 'Times New Roman', serif" },
    { label: "Monospace", value: "'Courier New', Courier, monospace" },
];

const HEADING_OPTIONS = [
    { label: "Normal", value: 0 },
    { label: "H1", value: 1 },
    { label: "H2", value: 2 },
    { label: "H3", value: 3 },
    { label: "H4", value: 4 },
    { label: "H5", value: 5 },
    { label: "H6", value: 6 },
];

// ─── Table Insert Popup ──────────────────────────────────────────────────────
const TableInsertPopup = ({ onInsert, onClose, buttonRef }) => {
    const [hoveredRow, setHoveredRow] = useState(-1);
    const [hoveredCol, setHoveredCol] = useState(-1);
    const popupRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                popupRef.current &&
                !popupRef.current.contains(e.target) &&
                buttonRef.current &&
                !buttonRef.current.contains(e.target)
            ) {
                onClose();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose, buttonRef]);

    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return null;

    return (
        <div
            ref={popupRef}
            className="tiptap-table-popup"
            style={{ position: "fixed", top: `${rect.bottom + 4}px`, left: `${rect.left}px` }}
        >
            <div className="tiptap-table-popup-title">Insert Table</div>
            <div className="tiptap-table-popup-grid">
                {Array.from({ length: 8 }, (_, r) =>
                    Array.from({ length: 8 }, (_, c) => (
                        <div
                            key={`${r}-${c}`}
                            className={`tiptap-table-popup-cell ${r <= hoveredRow && c <= hoveredCol ? "active" : ""}`}
                            onMouseOver={() => { setHoveredRow(r); setHoveredCol(c); }}
                            onClick={() => { onInsert(r + 1, c + 1); onClose(); }}
                        />
                    ))
                )}
            </div>
            <div className="tiptap-table-popup-size">
                {hoveredRow >= 0 ? `${hoveredRow + 1} × ${hoveredCol + 1}` : "Select size"}
            </div>
        </div>
    );
};

// ─── Toolbar Component ──────────────────────────────────────────────────────
const EditorToolbar = ({ editor }) => {
    const [showTablePopup, setShowTablePopup] = useState(false);
    const tableButtonRef = useRef(null);

    if (!editor) return null;

    const handleInsertTable = (rows, cols) => {
        editor.chain().focus().insertTable({ rows, cols, withHeaderRow: false }).run();
    };

    const handleSetLink = () => {
        const previousUrl = editor.getAttributes("link").href;
        let url = window.prompt("Enter URL", previousUrl || "https://");
        if (url === null) return;
        if (url === "") {
            editor.chain().focus().extendMarkRange("link").unsetLink().run();
            return;
        }
        if (!/^https?:\/\//i.test(url)) {
            url = "https://" + url;
        }
        editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    };


    const handleAddImage = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = (e) => {
            const file = e.target.files[0];
            if (file) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    editor.chain().focus().setImage({ src: e.target.result }).run();
                };
                reader.readAsDataURL(file);
            }
        };
        input.click();
    };

    const currentFontSize = editor.getAttributes("textStyle")?.fontSize || "";
    const currentFontFamily = editor.getAttributes("textStyle")?.fontFamily || "";

    const getActiveHeading = () => {
        for (let i = 1; i <= 6; i++) {
            if (editor.isActive("heading", { level: i })) return i;
        }
        return 0;
    };

    return (
        <div className="tiptap-toolbar">
            {/* Font Family */}
            <div className="tiptap-toolbar-group">
                <select
                    value={currentFontFamily}
                    onChange={(e) => {
                        if (e.target.value) {
                            editor.chain().focus().setFontFamily(e.target.value).run();
                        } else {
                            editor.chain().focus().unsetFontFamily().run();
                        }
                    }}
                    title="Font Family"
                >
                    {FONT_FAMILY_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            </div>

            {/* Font Size */}
            <div className="tiptap-toolbar-group">
                <select
                    value={currentFontSize}
                    onChange={(e) => {
                        if (e.target.value) {
                            editor.chain().focus().setFontSize(e.target.value).run();
                        } else {
                            editor.chain().focus().unsetFontSize().run();
                        }
                    }}
                    title="Font Size"
                >
                    <option value="">Size</option>
                    {FONT_SIZE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            </div>

            {/* Bold, Italic, Underline, Strike */}
            <div className="tiptap-toolbar-group">
                <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={editor.isActive("bold") ? "is-active" : ""} title="Bold"><b>B</b></button>
                <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={editor.isActive("italic") ? "is-active" : ""} title="Italic"><i>I</i></button>
                <button type="button" onClick={() => editor.chain().focus().toggleUnderline().run()} className={editor.isActive("underline") ? "is-active" : ""} title="Underline"><u>U</u></button>
                <button type="button" onClick={() => editor.chain().focus().toggleStrike().run()} className={editor.isActive("strike") ? "is-active" : ""} title="Strikethrough"><s>S</s></button>
            </div>

            {/* Text Color & Background */}
            <div className="tiptap-toolbar-group">
                <input
                    type="color"
                    className="tiptap-color-input"
                    onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
                    value={editor.getAttributes("textStyle")?.color || "#000000"}
                    title="Text Color"
                />
                <input
                    type="color"
                    className="tiptap-color-input"
                    onChange={(e) => editor.chain().focus().toggleHighlight({ color: e.target.value }).run()}
                    value={editor.getAttributes("highlight")?.color || "#ffffff"}
                    title="Background Color"
                />
            </div>

            {/* Subscript & Superscript */}
            <div className="tiptap-toolbar-group">
                <button type="button" onClick={() => editor.chain().focus().toggleSubscript().run()} className={editor.isActive("subscript") ? "is-active" : ""} title="Subscript">X₂</button>
                <button type="button" onClick={() => editor.chain().focus().toggleSuperscript().run()} className={editor.isActive("superscript") ? "is-active" : ""} title="Superscript">X²</button>
            </div>

            {/* Heading */}
            <div className="tiptap-toolbar-group">
                <select
                    value={getActiveHeading()}
                    onChange={(e) => {
                        const level = parseInt(e.target.value);
                        if (level === 0) {
                            editor.chain().focus().setParagraph().run();
                        } else {
                            editor.chain().focus().toggleHeading({ level }).run();
                        }
                    }}
                    title="Heading Level"
                >
                    {HEADING_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            </div>

            {/* Alignment */}
            <div className="tiptap-toolbar-group">
                <button type="button" onClick={() => editor.chain().focus().setTextAlign("left").run()} className={editor.isActive({ textAlign: "left" }) ? "is-active" : ""} title="Align Left">⫷</button>
                <button type="button" onClick={() => editor.chain().focus().setTextAlign("center").run()} className={editor.isActive({ textAlign: "center" }) ? "is-active" : ""} title="Align Center">≡</button>
                <button type="button" onClick={() => editor.chain().focus().setTextAlign("right").run()} className={editor.isActive({ textAlign: "right" }) ? "is-active" : ""} title="Align Right">⫸</button>
                <button type="button" onClick={() => editor.chain().focus().setTextAlign("justify").run()} className={editor.isActive({ textAlign: "justify" }) ? "is-active" : ""} title="Justify">☰</button>
            </div>

            {/* Lists & Indent */}
            <div className="tiptap-toolbar-group">
                <button type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()} className={editor.isActive("orderedList") ? "is-active" : ""} title="Ordered List">1.</button>
                <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()} className={editor.isActive("bulletList") ? "is-active" : ""} title="Bullet List">•</button>
            </div>

            {/* Link */}
            <div className="tiptap-toolbar-group">
                <button type="button" onClick={handleSetLink} className={editor.isActive("link") ? "is-active" : ""} title="Insert Link"><Attachment width={14} height={20} /></button>
                <button type="button" onClick={handleAddImage} className={editor.isActive("image") ? "is-active" : ""} title="Insert Image"><ImageIcon size={18} /></button>
            </div>

            {/* Table */}
            <div className="tiptap-toolbar-group">
                <button type="button"
                    ref={tableButtonRef}
                    onClick={() => setShowTablePopup(!showTablePopup)}
                    title="Insert Table"
                    className={showTablePopup ? "is-active" : ""}
                >
                    ⊞
                </button>
                {editor.can().deleteTable() && (
                    <>
                        <button type="button" onClick={() => editor.chain().focus().addRowBefore().run()} title="Add Row Above">↑+</button>
                        <button type="button" onClick={() => editor.chain().focus().addRowAfter().run()} title="Add Row Below">↓+</button>
                        <button type="button" onClick={() => editor.chain().focus().addColumnBefore().run()} title="Add Col Left">←+</button>
                        <button type="button" onClick={() => editor.chain().focus().addColumnAfter().run()} title="Add Col Right">→+</button>
                        <button type="button" onClick={() => editor.chain().focus().deleteRow().run()} title="Delete Row">Row -</button>
                        <button type="button" onClick={() => editor.chain().focus().deleteColumn().run()} title="Delete Column">Col -</button>
                        <button type="button" onClick={() => editor.chain().focus().deleteTable().run()} title="Delete Table"><DeleteDustbin /></button>
                        <button type="button" onClick={() => {
                            const isBorderless = editor.getAttributes('table').borderless;
                            editor.chain().focus().updateAttributes('table', { borderless: !isBorderless }).run();
                        }} className={editor.getAttributes('table').borderless ? "is-active" : ""} title="Toggle Borders">🔲 {editor.getAttributes('table').borderless ? "Hide" : "Show"}</button>
                    </>
                )}
            </div>

            {/* Blockquote & Code */}
            <div className="tiptap-toolbar-group">
                <button type="button" onClick={() => editor.chain().focus().toggleBlockquote().run()} className={editor.isActive("blockquote") ? "is-active" : ""} title="Blockquote">❝</button>
                <button type="button" onClick={() => editor.chain().focus().toggleCodeBlock().run()} className={editor.isActive("codeBlock") ? "is-active" : ""} title="Code Block">{"</>"}</button>
            </div>

            {/* Clear Formatting */}
            <div className="tiptap-toolbar-group">
                <button type="button" onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()} title="Clear Formatting">✕</button>
            </div>

            {showTablePopup && (
                <TableInsertPopup
                    onInsert={handleInsertTable}
                    onClose={() => setShowTablePopup(false)}
                    buttonRef={tableButtonRef}
                />
            )}
        </div>
    );
};

// ─── Main TipTapEditor Component ─────────────────────────────────────────────
const TipTapEditor = forwardRef(({
    value = "",
    onChange,
    onFocus,
    onBlur,
    className = "",
    style = {},
    placeholder = "",
    contentClassName = "",
}, ref) => {
    const isInternalUpdate = useRef(false);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: { levels: [1, 2, 3, 4, 5, 6] },
            }),
            TextAlign.configure({
                types: ["heading", "paragraph"],
                alignments: ["left", "center", "right", "justify"],
            }),
            Underline,
            TextStyle,
            Color,
            FontFamily,
            FontSize,
            Highlight.configure({ multicolor: true }),
            Link.configure({
                openOnClick: false,
                HTMLAttributes: { target: "_blank", rel: "noopener noreferrer" },
            }),
            ImageResize.configure({ allowBase64: true, inline: true }),
            Subscript,
            Superscript,
            CustomTable.configure({ resizable: true }),
            TableBorderExtension,
            TableRow,
            TableCell,
            TableHeader,
        ],
        content: value || "",
        editorProps: {
            transformPastedHTML(html) {
                if (!html) return html;
                try {
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(html, 'text/html');
                    doc.querySelectorAll('*').forEach(el => {
                        el.style.removeProperty('color');
                        el.style.removeProperty('background-color');
                        el.style.removeProperty('background');
                    });
                    return doc.body.innerHTML;
                } catch (e) {
                    return html;
                }
            }
        },
        onUpdate: ({ editor: editorInstance }) => {
            if (isInternalUpdate.current) return;
            let html = editorInstance.getHTML();

            // Inject inline styles for borderless tables
            if (html.includes('data-borderless="true"')) {
                try {
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(html, 'text/html');
                    const borderlessTables = doc.querySelectorAll('table[data-borderless="true"]');
                    borderlessTables.forEach(table => {
                        const cells = table.querySelectorAll('td, th');
                        cells.forEach(cell => {
                            cell.style.setProperty('border', '0px', 'important');
                        });
                    });
                    html = doc.body.innerHTML;
                } catch (e) {
                    console.error("Error processing table styles:", e);
                }
            }

            if (onChange) onChange(html);
        },
        onFocus: () => {
            if (onFocus) onFocus();
        },
        onBlur: () => {
            if (onBlur) onBlur();
        },
        editorProps: {
            attributes: {
                class: "tiptap",
            },
        },
    });

    // Sync external value changes
    useEffect(() => {
        if (!editor || editor.isDestroyed) return;
        const currentHTML = editor.getHTML();
        if (value !== currentHTML && value !== undefined) {
            isInternalUpdate.current = true;
            editor.commands.setContent(value || "", false);
            isInternalUpdate.current = false;
        }
    }, [value, editor]);

    // Expose editor methods via ref
    useImperativeHandle(ref, () => ({
        getHTML: () => editor?.getHTML() || "",
        setHTML: (html) => {
            if (!editor || editor.isDestroyed) return;
            isInternalUpdate.current = true;
            editor.commands.setContent(html || "", false);
            isInternalUpdate.current = false;
        },
        getText: () => editor?.getText() || "",
        getEditor: () => editor,
        focus: () => editor?.chain().focus().run(),
        clear: () => {
            if (!editor || editor.isDestroyed) return;
            isInternalUpdate.current = true;
            editor.commands.setContent("", false);
            isInternalUpdate.current = false;
        },
    }), [editor]);

    return (
        <div className={`tiptap-editor-wrapper ${className}`} style={style}>
            <EditorToolbar editor={editor} />
            <div className={`tiptap-editor-content ${contentClassName}`}>
                <EditorContent editor={editor} />
            </div>
        </div>
    );
});

TipTapEditor.displayName = "TipTapEditor";

export default TipTapEditor;
export { FontSize, FONT_SIZE_OPTIONS, FONT_FAMILY_OPTIONS, HEADING_OPTIONS, EditorToolbar, TableInsertPopup };












